import utils from '../utils';

export interface InstructionsOptions {
  /** Whether to announce regions or not - Default 'off' */
  ariaLive?: 'assertive' | 'off' | 'polite' | 'alert';
  /**
   * What parts of the instructions to announce.
   * {number} announces questionText[number]
   * {number[]} announces questionText[...numbers]
   * {string} announces that specific string
   * Default: 'all' announces entire instructionsDiv
   */
  ariaLiveRegion?: 'all' | number | number[];
  /**
   * {string | function} to generate aria title text -- overrides any
   * setting in ariaTitleRegion, defaults false.
   */
  ariaLiveTitle?: string | ((questionText: string[]) => string);
}

export interface PriorInstructions {
  questionPrompt: string;
  questionText: string[];
  innerHTML?: string;
}

export function displayInstructions(
  instructionsDiv: HTMLDivElement,
  questionPrompt: string,
  questionText: string[],
  options: InstructionsOptions,
  priorInstructions?: PriorInstructions
): PriorInstructions {
  if (priorInstructions && priorInstructions.questionPrompt === questionPrompt) {
    // If the questionPrompt matches the old one, we can use the old element and pull out
    // and update just those areas we need to update.
    updateInstructionsElements(instructionsDiv, questionText, options, priorInstructions);
  } else {
    // Otherwise, we need to create the elements for the first time
    createInstructionsElements(instructionsDiv, questionPrompt, questionText, options);
  }

  return {
    questionPrompt,
    questionText,
    innerHTML: instructionsDiv.innerHTML,
  };
}

function getQuestionPartsAndIndexes(questionPrompt: string) {
  const regex = /\{question(?:\[\d+\])?\}/g;
  const indexRegex = /(?:\[)(\d+)(?:\])/;

  const promptParts = questionPrompt.split(regex);
  const indexes = (questionPrompt.match(regex) || []).map((questionArea, index) => {
    const match = questionArea.match(indexRegex);
    return match ? Number(match[1]) : index;
  });
  return { promptParts, indexes };
}

function createInstructionsElements(
  instructionsDiv: HTMLDivElement,
  questionPrompt: string,
  questionText: string[],
  options: InstructionsOptions
) {
  turnOffAllAriaLive(instructionsDiv);
  const { promptParts, indexes } = getQuestionPartsAndIndexes(questionPrompt);
  const innerHTML = promptParts.join('<span class="question-text"></span>');
  setAriaLive(instructionsDiv, 'all', options);
  instructionsDiv.innerHTML = innerHTML;
  const questionSpans: NodeListOf<HTMLSpanElement> =
    instructionsDiv.querySelectorAll('span.question-text');
  utils.nodeListForEach(questionSpans, (span, index) => {
    setAriaLive(span, index, options);
    span.innerHTML = questionText[indexes[index]];
  });
  if (options.ariaLiveTitle) {
    const titleSpan = insertTitleSpan(instructionsDiv);
    titleSpan.innerHTML = utils.isFunction(options.ariaLiveTitle)
      ? options.ariaLiveTitle(questionText)
      : options.ariaLiveTitle;
  }
}

function updateInstructionsElements(
  instructionsDiv: HTMLDivElement,
  questionText: string[],
  options: InstructionsOptions,
  priorInstructions: PriorInstructions
) {
  turnOffAllAriaLive(instructionsDiv);
  setAriaLive(instructionsDiv, 'all', options);
  // Update the aria-title span, if needed
  if (options.ariaLiveTitle) {
    let titleSpan: HTMLSpanElement = instructionsDiv.querySelector('span.instructions-aria-title');
    if (!titleSpan) {
      titleSpan = insertTitleSpan(instructionsDiv);
    }
    setAriaLive(titleSpan, 'title', options);
    titleSpan.innerHTML = utils.isFunction(options.ariaLiveTitle)
      ? options.ariaLiveTitle(questionText)
      : options.ariaLiveTitle;
  }
  // Update each question region, setting aria-live as indicated
  const questionSpans: NodeListOf<HTMLSpanElement> =
    instructionsDiv.querySelectorAll('span.question-text');
  const { indexes } = getQuestionPartsAndIndexes(priorInstructions.questionPrompt);
  utils.nodeListForEach(questionSpans, (span, index) => {
    if (span.innerHTML === questionText[index]) return; // no change has been made
    setAriaLive(span, index, options);
    span.innerHTML = questionText[indexes[index]];
  });
}

function insertTitleSpan(instructionsDiv: HTMLDivElement) {
  const span = document.createElement('span');
  span.classList.add('sr-only', 'instructions-aria-title');
  instructionsDiv.insertBefore(span, instructionsDiv.firstChild);
  return span;
}

function turnOffAllAriaLive(instructionsDiv: HTMLDivElement) {
  instructionsDiv.removeAttribute('aria-live');
  instructionsDiv.removeAttribute('role');
  const questionSpans = instructionsDiv.querySelectorAll('span.question-text');
  utils.nodeListForEach(questionSpans, (span) => {
    span.removeAttribute('aria-live');
    span.removeAttribute('role');
  });
}

function setAriaLive(
  el: HTMLElement,
  index: 'all' | 'title' | number,
  options: InstructionsOptions
) {
  const { ariaLiveRegion = 'all', ariaLive = 'off', ariaLiveTitle } = options;
  // If aria is off, no reason to set anything.
  if (ariaLive === 'off') return;
  const indexMatch = index === ariaLiveRegion;
  const titleMatch = !!ariaLiveTitle && index === 'title';
  const arrayMatch =
    utils.isArray(ariaLiveRegion) && typeof index === 'number' && ariaLiveRegion.includes(index);
  if (indexMatch || titleMatch || arrayMatch) {
    if (ariaLive === 'alert') {
      el.setAttribute('role', 'alert');
      return;
    }
    el.setAttribute('aria-live', ariaLive);
  }
}
